import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../components/DataTable/DataTable";
import {API} from "../../types/API";
import {DataTableRequest} from "../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {ErrorHandler} from "../../hooks/errorHandler";
import {useToastContext} from "../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {ActivitiesResponse, Log, LogsResponse} from "../../types/Responses/LogsResponse";
import useComponentVisible from "../../hooks/useComponentVisible";
import BrokerAvatar from "../../components/BrokerAvatar";
import Modal from "../../components/Modal/Modal";
import ProviderAvatar from "../../components/ProviderAvatar";
import {UserListResponse} from "../../types/Responses/UsersResponse";
import TableSkeleton from "../../components/Skeletons/TableSkeleton";
import {useNavigate, useParams} from "react-router-dom";
import CountryAvatar from "../../components/CountryAvatar";
import {useTranslation} from "react-i18next";
import Card from "../../components/Card";
import {setTitle} from "../../hooks/setTitle";
import {APILink} from "../../types/APILink";
import {checkEnabled2FA} from "../../hooks/checkEnabled2FA";
import {useUserContext} from "../../types/contexts/UserContext";
import usePersistedDataTableRequest from "../../hooks/usePersistedDataTableRequest";
import {setSidebarTitle} from "../../reducers/sidebar";
import {useDispatch} from "react-redux";
import FilterChips, {FilterChipDefinition} from "../../components/FilterChips";
import DropdownInput from "../../components/DropdownInput";

type LogsDataTableRequest = DataTableRequest & { user?: number, activity?: number };

const Logs = () => {
    const {t} = useTranslation();
    const {id} = useParams();
    const toast = useToastContext();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const handleError = useErrorHandler();
    const user = useUserContext();
    const [users, setUsers] = useState<UserListResponse>();
    const [activities, setActivities] = useState<ActivitiesResponse>();
    const [logs, setLogs] = useState<LogsResponse>();
    const [modalLog, setModalLog] = useState<Log>();
    const {ref, isComponentVisible, setIsComponentVisible} = useComponentVisible(false);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const defaultRequest = useMemo<LogsDataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 1
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<LogsDataTableRequest>(defaultRequest);
    const filterUser = dataTableRequest.user ?? 0;
    const filterActivity = dataTableRequest.activity ?? 0;

    const clearRequest = (request: DataTableRequest | object) => {
        const keys = Object.keys(request);
        const tempRequest = {...request} as DataTableRequest;
        if (keys.includes('user') && typeof request['user'] !== "undefined" && parseInt(request['user']) === 0)
            delete tempRequest['user'];
        if (keys.includes('activity') && typeof request['activity'] !== "undefined" && parseInt(request['activity']) === 0)
            delete tempRequest['activity'];
        return tempRequest;
    }

    const openModal = useCallback((log: Log) => {
        setModalLog(log);
        setIsComponentVisible(true);
    }, [setModalLog, setIsComponentVisible]);

    const sendRequest = useCallback((request: object) => {
        setIsDataTableLoading(true);
        API.getLogs(clearRequest(request)).then((response: AxiosResponse) => {
            setLogs(response.data as LogsResponse);
            setIsDataTableLoading(false);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_LOGS}`, toast, handleError));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: LogsDataTableRequest) => {
        request = {
            ...request,
            user: filterUser,
            activity: filterActivity
        }
        persistDataTableRequest(request);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, filterUser, filterActivity, sendRequest]);

    const getUsers = useCallback(() => {
        API.getUsersList(1).then((response: AxiosResponse) => {
            setUsers(response.data as UserListResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_USERS_LIST}`, toast, handleError));
    }, [toast, handleError]);

    const getActivities = useCallback(() => {
        API.getLogsActivities().then((response: AxiosResponse) => {
            setActivities(response.data as ActivitiesResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_LOGS_ACTIVITIES}`, toast, handleError));
    }, [toast, handleError]);

    const onUserChanged = useCallback((u: string | number) => {
        const parsedUser = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            user: parsedUser,
            activity: filterActivity
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, filterActivity, sendRequest, persistDataTableRequest]);

    const onActivityChanged = useCallback((u: string | number) => {
        const parsedActivity = typeof u === "string" ? parseInt(u) : u;
        const request = {
            ...dataTableRequest,
            page: 1,
            activity: parsedActivity,
            user: filterUser
        };
        persistDataTableRequest(request);
        sendRequest(request);
    }, [dataTableRequest, filterUser, sendRequest, persistDataTableRequest]);

    const closeModal = useCallback(() => {
        setIsComponentVisible(false);
    }, [setIsComponentVisible]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.logs.title'));
        dispatch(setSidebarTitle(t('admin.logs.title')))
        if (user && user.user){
            checkEnabled2FA(user.user).then(() => {
                const routeUser = id ? parseInt(id) : undefined;
                const request = (!dataTableRequest.user && routeUser)
                    ? {...dataTableRequest, user: routeUser}
                    : dataTableRequest;
                if (!dataTableRequest.user && routeUser) {
                    persistDataTableRequest(request);
                }
                sendRequest(request);
                getUsers();
                getActivities();
            }).catch();
        }
    }, [user, id, dispatch]);


    const filterChipsItems = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (users?.data && filterUser > 0) {
            const selectedUser = users.data.find((u) => parseInt(u.id) === filterUser);
            if (selectedUser) {
                chips.push({
                    key: `user-${selectedUser.id}`,
                    label: `${t('admin.logs.table.user')}: ${selectedUser.name}`,
                    onRemove: () => onUserChanged('0')
                });
            }
        }
        if (activities?.data && filterActivity > 0) {
            const activity = activities.data.find((a) => a.id === filterActivity);
            if (activity) {
                chips.push({
                    key: `activity-${activity.id}`,
                    label: `${t('admin.logs.table.activity')}: ${activity.name}`,
                    onRemove: () => onActivityChanged('0')
                });
            }
        }
        return chips;
    }, [users, filterUser, activities, filterActivity, t, onUserChanged, onActivityChanged]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
            </div>
        )
    }, [user, refresh]);

    const filters = useMemo(() => {
        if (!users || !activities)
            return <></>;

        return <>
            <DropdownInput
                searchable={true}
                withAllButton={true}
                name={t('admin.logs.table.user')}
                selected={filterUser}
                onChange={onUserChanged}
                onSelectAll={() => onUserChanged('')}
                list={users.data.map((u) => {
                    return {name: u.name, id: parseInt(u.id)}
                })}
                menuClassName="w-full -mt-5"
            />
            <DropdownInput
                withAllButton={true}
                searchable={true}
                name={t('admin.logs.table.activity')}
                selected={filterActivity}
                onChange={onActivityChanged}
                onSelectAll={() => onActivityChanged('')}
                list={activities.data}
                menuClassName="w-full -mt-5"
            />
        </>
    }, [users, activities, filterUser, filterActivity, onUserChanged, onActivityChanged, t]);

    const modalUI = useMemo(() => {
        if (!modalLog || !isComponentVisible)
            return <></>;

        return <Modal refer={ref} showModal={isComponentVisible} title={modalLog.activity}>
            <div className="w-full">
                <FontAwesomeIcon icon={solid('xmark')} size="lg"
                                 className="cursor-pointer absolute top-8 right-4 dark:text-white"
                                 onClick={() => closeModal()}/>
                <div className={`grid mb-8 mt-8 ${modalLog.country ? 'grid-cols-3' : 'grid-cols-2'}`}>
                    {modalLog.broker ? <div>
                        <span className="cursor-pointer hover:text-teal-500" onClick={() => navigate(`/app/admin/users/${modalLog?.broker?.id}/profile`)}>
                            <BrokerAvatar size="xs" image={modalLog.broker.image}/>
                            {modalLog.broker.name}
                        </span>
                    </div> : ''}
                    {modalLog.method ? <div>
                        <ProviderAvatar size="xs"
                                        image={`${window.location.origin}/images/psps/${modalLog.method.image}`}/>
                        {modalLog.method.name}
                    </div> : ''}
                    {modalLog.country ? <div>
                        <CountryAvatar country={modalLog.country}/>
                    </div> : ''}
                </div>
                <div className="mb-8 text-center">
                    {modalLog.browser}
                </div>
                {modalLog.difference ? <table className="w-full table-auto text-left">
                    <thead>
                    <tr>
                        <th className="w-[20%] p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{t('admin.logs.modal.field')}</th>
                        <th className="w-[40%] p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{t('admin.logs.modal.old_value')}</th>
                        <th className="w-[40%] p-4 border-b border-blue-gray-50 dark:border-blue-gray-500">{t('admin.logs.modal.new_value')}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {modalLog.difference.map(d => <tr key={d.key}>
                        <TD className="w-[20%] text-left">{d.key}</TD>
                        <TD className="w-[40%] text-left">{<>{d.old}</>}</TD>
                        <TD className="w-[40%] text-left">{<>{d.updated}</>}</TD>
                    </tr>)}
                    </tbody>
                </table> : ''}
            </div>
        </Modal>
    }, [modalLog, isComponentVisible, closeModal, ref]);

    const dataTable = useMemo(() => {
        if (!logs)
            return <TableSkeleton/>;

        return <DataTable {...dataTableRequest}
                          total_pages={logs.pages.total}
                          count={logs.count}
                          nodata={logs.data.length === 0}
                          filters={filters}
                          fullWidthFilters={<FilterChips className="px-4 -mt-6 mb-6" items={filterChipsItems}/>}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.logs.table.user'), sortBy: 'user_id'},
            {name: t('admin.logs.table.user_roles')},
            {name: t('admin.logs.table.activity'), sortBy: 'activity_id'},
            {name: t('admin.logs.table.ip'), sortBy: 'ip'},
            {name: t('admin.logs.table.date'), sortBy: 'created_at'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {logs.data.map((log, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD>
                            <span className="cursor-pointer hover:text-teal-500 flex items-center justify-center" onClick={() => navigate(`/app/admin/users/${log.user.id}/profile`)}>
                                <BrokerAvatar image={log.user.image}/>
                                {log.user.name}
                            </span>
                        </TD>
                        <TD>{log.user.roles?.join(',') ?? ''}</TD>
                        <TD>{log.activity}</TD>
                        <TD>{log.ip}</TD>
                        <TD>{log.date}</TD>
                        <TD className="text-right">
                            {log.difference ? <Button onClick={() => openModal(log)}
                                                      className="px-5 py-2 text-white bg-black mr-2"><FontAwesomeIcon
                                icon={solid("eye")}/></Button> : ''}
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, logs, filters, filterChipsItems, onDataTableChanged, openModal]);

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {modalUI}
                    {dataTable}
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Logs);
